// @flow

// test compilation fails on TripSearchGeoPill (likely because it's in roots instead of packages)
// we'll have to rely on the sub-component tests for now
/* eslint-disable ta-code-conventions/has-matching-test */

/**
 * Displays the homepage hero with geo pill and optional sponsored profile.
 *
 * @author rbernard
 * @since Jan 2019
 */

import * as React from 'react';

// absolute imports
import { bind } from '@ta/common.api';
import { featureIsEnabled } from '@ta/common.features';
import { type PhotoInfo, type MediaSize } from '@ta/media.common';
import { getSource, LazyImage } from '@ta/media.image';
import { TrackingNamespaceContext, HomeTrackingContext } from '@ta/tracking.interactions';
import TripSearchGeoPill from 'brand.trip-search-geopill';
import HeroValueProp from '@ta/homepage.hero-value-prop';
import classNames from '@ta/common.classnames';

// relative imports
import HeroPhoto from './HeroPhoto';
import HeroVideo from './HeroVideo';
import GeoPin from './GeoPin';
import SponsoredProfile from './SponsoredProfile';
import SponsoredHomeProvider, { isValidSponsorship } from './SponsoredHomeProvider';
import SponsoredHomepageHeroInfo from './SponsoredHomepageHeroInfo';
import { getLatencyTracker } from './hero-latency-tracking';
import type { VideoInfo, Sponsorship, HomepageHeroServlet } from './types';
import styles from './HomepageHero.less';

type ExternalProps = {|
  locationId?: number,
  photo?: PhotoInfo,
  video?: VideoInfo,
  sponsorship?: Sponsorship,
  servlet?: HomepageHeroServlet,
  hideGeoPill?: boolean,
  fullWidthSponsor?: boolean,
|};

type Props = {|
  isMobile: boolean,
  puid: string,
  ...ExternalProps,
|};

const getPoster = (width: number, height: number, posterSizes: ?Array<MediaSize>) =>
  (posterSizes &&
    getSource(width, height, posterSizes, {
      getImageBasedOnClientInfo: true,
      allowedStretch: 0.25,
    })) || {
    url: '',
    width: 0,
    height: 0,
  };

// Doubleclick pixel is permitted to be load without waiting for page load, TA pixels are assumed to be doubleclick
const DOUBLECLICK_URL_PREFIX = 'https://ad.doubleclick.net/ddm';

const parsePixel = (pixel: string): string => {
  let parsedPixel = pixel.replace('[timestamp]', new Date().getTime().toString());
  parsedPixel = parsedPixel.replace('[migRandom]', Math.floor(Math.random() * 20).toString());
  return parsedPixel;
};

const HomepageHero = ({
  isMobile,
  puid,
  locationId,
  photo,
  video,
  sponsorship: externalSponsorship,
  servlet,
  hideGeoPill,
  fullWidthSponsor,
}: Props) => (
  <TrackingNamespaceContext.Provider value="home">
    <HomeTrackingContext.Provider value={{ uid: puid, scopedGeoId: locationId }}>
      <SponsoredHomeProvider sponsorship={externalSponsorship} servlet={servlet}>
        {(sponsorship?: Sponsorship, loading?: boolean) => {
          const isSponsored = isValidSponsorship(sponsorship);
          const sponsoredPhoto =
            isSponsored && sponsorship && sponsorship.photo && sponsorship.photo.photoSizes
              ? sponsorship.photo
              : null;
          const sponsoredVideo =
            isSponsored && sponsorship && sponsorship.video && sponsorship.video.sources
              ? sponsorship.video
              : null;
          let heroPhoto;
          let heroVideo;
          if (!loading) {
            heroPhoto = sponsoredPhoto || photo;
            heroVideo = sponsoredVideo || video;
          }
          const poster = getPoster(
            1024,
            250,
            (heroVideo && heroVideo.posterSizes) || (heroPhoto && heroPhoto.photoSizes),
          );
          let backgroundStyles;
          let latencyTracker;
          if (poster && poster.url) {
            // apply background poster photo here so that any client-side re-rendering of full size photo/video looks seamless
            // ResponsiveImage can load this same poster server-side via the ssrContainerDimensions prop, but it would flash for a second when it starts to load the full size photo client-side
            // VideoPlayer does support a poster photo, but this way loads it faster and gives us more control
            backgroundStyles = { backgroundImage: `url(${poster.url})` };
            if (featureIsEnabled('mvp_hero_latency_tracking')) {
              // Load a duplicate hero inline (1x1) to track latency
              latencyTracker = getLatencyTracker(poster.url);
            }
          }
          const { impressionTrackers = [] } = sponsorship || {};
          const combineHomepageHeroInfo = featureIsEnabled('t10669_merged_hph_element');

          return (
            <div className={styles.homepageHero}>
              <div className={styles.hero} style={backgroundStyles}>
                {!loading && (
                  <React.Fragment>
                    {heroVideo && !isMobile ? (
                      <HeroVideo video={heroVideo} />
                    ) : (
                      <HeroPhoto photo={heroPhoto} locationId={locationId} />
                    )}
                  </React.Fragment>
                )}
              </div>
              {latencyTracker}
              {!hideGeoPill &&
                (featureIsEnabled('cx_single_search_variant1') ||
                featureIsEnabled('cx_single_search_variant2') ||
                featureIsEnabled('cx_single_search_home') ? (
                  <div className={styles.valuePropContainer}>
                    <HeroValueProp />
                  </div>
                ) : (
                  <div className={styles.geoPillContainer}>
                    <TripSearchGeoPill />
                  </div>
                ))}
              {isSponsored && sponsorship != null && (
                <div
                  className={classNames(
                    styles.sponsor,
                    featureIsEnabled('cx_single_search_variant2')
                      ? styles.cx_single_search_variant2
                      : '',
                  )}
                >
                  <div
                    className={classNames(
                      styles.sponsorWrapper,
                      featureIsEnabled('cx_single_search_variant2')
                        ? styles.cx_single_search_variant2
                        : '',
                      {
                        [styles.fullWidthSponsorWrapper]: fullWidthSponsor,
                        [styles.combinedInfo]: combineHomepageHeroInfo,
                      },
                    )}
                  >
                    {combineHomepageHeroInfo ? (
                      <div className={styles.sponsorColumn}>
                        <SponsoredHomepageHeroInfo
                          profileAvatar={sponsorship.profileAvatar}
                          profile={sponsorship.profile}
                          destination={sponsorship.destination}
                          url={sponsorship.url}
                        />
                      </div>
                    ) : (
                      <>
                        <div className={styles.sponsorColumn}>
                          {/* display empty sponsorColumn div even if no geo pin in order to right-align profile */}
                          {sponsorship.destination ? (
                            <GeoPin location={sponsorship.destination} />
                          ) : null}
                        </div>
                        <div className={styles.sponsorColumn}>
                          <SponsoredProfile sponsorship={sponsorship} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {impressionTrackers
                .filter(Boolean)
                .map(pixel =>
                  pixel.startsWith(DOUBLECLICK_URL_PREFIX) ? (
                    <img
                      suppressHydrationWarning
                      className={styles.trackingPixel}
                      src={parsePixel(pixel)}
                      alt=""
                    />
                  ) : (
                    <LazyImage className={styles.trackingPixel} src={parsePixel(pixel)} alt="" />
                  ),
                )}
            </div>
          );
        }}
      </SponsoredHomeProvider>
    </HomeTrackingContext.Provider>
  </TrackingNamespaceContext.Provider>
);

HomepageHero.defaultProps = {
  locationId: undefined,
  photo: undefined,
  video: undefined,
  sponsorship: undefined,
  hideGeoPill: false,
};

/**
 * Stage the TripSearchGeoPill
 *
 * @param state The state in the Redux store
 * @param stage The API staging function
 */
export default bind<Props, ExternalProps, _, _, _, _>((state, stage) => {
  TripSearchGeoPill.stage(state, stage);
  return () => ({
    locationId: state.route.geo || 1,
    isMobile: state.meta.device.viewportCategory === 'MOBILE',
    puid: state.tracking.uid || '',
  });
})(HomepageHero);
